import clsx from 'clsx'
import { Helmet } from "react-helmet"
import { useLocation } from "react-router-dom"
import React, { useEffect, useState } from "react"
import { ListboxButton, ListboxOption } from '@headlessui/react'

import { IDAM } from "../../api/Registry"
import { ErrorState } from "../errors/ErrorState"
import { ListBox } from '../../lib/fragments/ListBox'
import { getStandardRoute } from "../../utils/Helpers"
import HttpServices from "../../services/HttpServices"
import { dataFormatsArray } from '../../utils/Constants'
import { LoadingXL } from "../../components/utils/LoadingXL"
import { usePageContext } from "../../lib/guards/PageContext"
import { AttributeRestrictions } from './AttributeRestrictions'
import { FloatingInput } from '../../components/utils/FloatingInput'
import { ModuleBreadCrumb } from "../../lib/fragments/ModuleBreadCrumb"
import { FloatingTextArea } from '../../components/utils/FloatingTextArea'
import { G_onInputBlurHandler, G_onInputChangeHandler } from '../../components/utils/InputHandlers'
import { CreateOrEditAttributeGroup } from '../users.attributes/CreateOrEditAttributeGroup'

export const CreateUserAttibute = () => {
    const [state, setstate] = useState({
        posting: false,
        httpStatus: 200,
        status: 'pending',
        stage: 'details',
        data: {
            groups: null
        },
        input: {
            attr_group: null,
            display_name: '',
            description: '',
            format: null,
            multiline: false,
            minimum: '3',
            maximum: '30',
            mandatory: false,
            display_style: null,
        },
        errors: {
            // Section 1
            details: '',
            attr_group: '',
            display_name: '',
            description: '',

            // Section 2
            configuration: '',
            format: '',
            minimum: '',
            maximum: '',
            display_style: '',

            // Section 3
            leadership: '',
        },
        show: {
            CreateAttributeGroup: false,
        },
        cargo: {
            uuid: null,
            action: 'create'
        }
    })

    const location = useLocation()
    const locationState = location.state
    const previousPath = locationState?.previous_path

    const pageTitle = "Create User Attribute"
    type ModalKeys = 'CreateAttributeGroup' | ''
    const { updateDataContext } = usePageContext()

    const formSections: Record<string, string[]> = {
        'details': ["attr_group", "display_name", "description"],
        'configuration': ["format",],
    }

    const breadCrumb = [
        { linkItem: false, title: 'IAM' },
        { linkItem: false, title: 'User Attributes' },
        { linkItem: false, title: pageTitle },
    ]

    const getAttributeGroups = async () => {
        let { data, httpStatus, status } = state

        try {
            const response: any = await HttpServices.httpGet(IDAM.ATTR_GROUPS)
            httpStatus = response.status

            if (httpStatus === 200) {
                const payload: any = response.data.payload
                data.groups = payload.groups

                status = 'fulfilled'
                dataContextHandler(status)

            } else {
                status = 'rejected'
            }
        } catch (error) {
            status = 'rejected'
            httpStatus = 500
        } finally {

        }

        setstate((prev: any) => ({
            ...prev,
            data,
            status,
            httpStatus,
        }))
    }

    useEffect(() => {
        dataContextHandler()
        getAttributeGroups()
    }, [])

    const toggleModal = (key: ModalKeys, value: boolean, uuid?: any) => {
        setstate((prevState: any) => ({
            ...prevState,
            show: {
                ...prevState.show,
                [key]: value,
                uuid: uuid,
            },
        }))
    }

    const dataContextHandler = (status: string = 'pending') => {
        const baseContext = {
            title: pageTitle,
            backButton: previousPath ?? getStandardRoute('USER.ATTR_'),
        }

        // Early return if data is not ready
        if (status !== 'fulfilled') {
            setTimeout(() => {
                updateDataContext(baseContext);
            }, 500)
            return
        }

        updateDataContext({
            ...baseContext,
        })
    }

    const onCompleteActionHandler = () => {
        getAttributeGroups()
    }

    const findSection = (fieldName: string) => {
        // Find section in which input belongs
        for (const [section, fields] of Object.entries(formSections)) {
            if (fields.includes(fieldName)) {
                return section;
            }
        }
        return null;
    }

    const sectionHasErrors = (section: string, errors: Record<string, string>) => {
        const fields = formSections[section] || [];
        // If any field still has an error
        return fields.some((field) => errors[field]);
    }

    const onListBoxHandler = (e: any, list: any) => {
        let { posting, data, errors, input } = state
        if (posting) return

        let updatedErrors = {
            ...errors
        }

        switch (list) {
            case 'attr_group':
                if (e === '__create_new__') {
                    // Show create new attribute group modal
                    toggleModal('CreateAttributeGroup', true)
                    return
                }

                const attrGroup = data.groups.find(
                    (grp: any) => grp.g === e
                )

                updatedErrors = {
                    ...updatedErrors,
                    attr_group: '',
                }

                setstate((prev: any) => ({
                    ...prev,
                    input: {
                        ...prev.input,
                        attr_group: attrGroup,
                    },
                    errors: updatedErrors
                }));
                break

            case 'format':
                updatedErrors = {
                    ...updatedErrors,
                    format: '',
                }

                let multiline = e.key === 'freetext' ? input.multiline : false

                setstate((prev: any) => ({
                    ...prev,
                    input: {
                        ...prev.input,
                        format: e,
                        multiline: multiline,
                    },
                    errors: updatedErrors
                }));
                break

            case 'display_style':
                if (input.format?.key !== 'predefined') {
                    setstate((prev: any) => ({
                        ...prev,
                        input: {
                            ...prev.input,
                            display_style: null,
                        },
                        errors: updatedErrors
                    }));
                } else {
                    updatedErrors = {
                        ...updatedErrors,
                        display_name: '',
                    }

                    setstate((prev: any) => ({
                        ...prev,
                        input: {
                            ...prev.input,
                            display_style: e,
                        },
                        errors: updatedErrors
                    }));
                }
                break;
        }

        let errorMsg = ''
        const sectionWithError = findSection(list)
        const stillHasErrors = sectionHasErrors(sectionWithError, updatedErrors)

        if (stillHasErrors) {
            errorMsg = 'Kindly clear errors in section before proceeding'
        }

        updatedErrors = {
            ...updatedErrors,
            [sectionWithError]: errorMsg
        }

        setstate((prev: any) => ({
            ...prev,
            errors: updatedErrors,
        }))
    }

    const onChangeHandler = (e: any) => {
        let { posting, input, errors } = state
        if (posting) return

        let output: any = G_onInputChangeHandler(e)

        const updatedInput = {
            ...input,
            [e.target.name]: output.value
        };

        const updatedErrors = {
            ...errors,
            [e.target.name]: output.error
        };

        setstate((prev: any) => ({
            ...prev,
            input: updatedInput,
            errors: updatedErrors,
        }));
    }

    const onInputBlur = (e: any) => {
        let { posting, input, errors } = state
        if (posting) return

        let errorMsg = ''
        let output: any = G_onInputBlurHandler(e, '')

        let updatedInput = {
            ...input,
        }

        let updatedErrors = {
            ...errors,
        };

        switch (e.target.name) {
            case 'minimum':
            case 'maximum':
                const format = input.format?.key

                if (!["freetext"].includes(format)) {
                    // TODO: Add validation for non-numeric and non-freetext
                    return
                }

                const targetName = e.target.name
                const targetValue = e.target.value

                if (format === 'freetext') {
                    const minLength = 3
                    const maxLength = 200

                    const num = Number(targetValue);
                    const isWholeNumber = Number.isInteger(num);

                    if (!isWholeNumber) {
                        updatedErrors = {
                            ...updatedErrors,
                            [targetName]: "Length must be a whole number."
                        }
                    } else {
                        if (targetName === "minimum") {
                            const newMin = num < minLength ? minLength
                                : num > maxLength ? maxLength
                                    : num

                            updatedInput = {
                                ...updatedInput,
                                minimum: newMin.toString(),
                                maximum:
                                    input.maximum && Number(input.maximum) < newMin
                                        ? ""
                                        : input.maximum,
                            };
                        }

                        if (targetName === "maximum") {
                            const newMax = num < minLength ? minLength
                                : num > maxLength ? maxLength
                                    : num

                            updatedInput = {
                                ...updatedInput,
                                maximum: newMax.toString(),
                                minimum:
                                    input.minimum && Number(input.minimum) > newMax
                                        ? minLength.toString()
                                        : input.minimum,
                            };
                        }
                    }
                }
                break;

            default:
                updatedInput = {
                    ...updatedInput,
                    [e.target.name]: output.value
                }
                break;
        }

        const sectionWithError = findSection(e.target.name)
        const stillHasErrors = sectionHasErrors(sectionWithError, errors)

        if (output.error || stillHasErrors) {
            errorMsg = 'Kindly clear errors in section before proceeding'
        }

        updatedErrors = {
            ...updatedErrors,
            [e.target.name]: output.error,
            [sectionWithError]: errorMsg
        };

        setstate((prev: any) => ({
            ...prev,
            input: updatedInput,
            errors: updatedErrors,
        }));
    }

    const goToStageHandler = (stage: string) => {
        let { posting } = state
        if (posting) return

        setstate((prev: any) => ({
            ...prev,
            stage: stage
        }));
    }

    const sectionValidationHandler = () => {
        let { posting, stage, input } = state
        if (posting) return

        let valid = true
        let inputKeys = null
        let errorKeys = null

        switch (stage) {
            case 'details':
                if (!input.attr_group) {
                    valid = false

                    setstate((prev: any) => ({
                        ...prev,
                        errors: {
                            ...prev.errors,
                            attr_group: 'Kindly select an attribute group',
                            details: 'Kindly clear errors in section before proceeding',
                        },
                    }));
                }

                if (!input.display_name) {
                    valid = false

                    setstate((prev: any) => ({
                        ...prev,
                        errors: {
                            ...prev.errors,
                            display_name: 'Display name is required',
                            details: 'Kindly clear errors in section before proceeding',
                        },
                    }));
                }

                // If validation of above fails exit
                if (!valid) return

                inputKeys = [
                    { name: 'display_name', required: true },
                    { name: 'description', required: false },
                ]

                inputKeys.forEach((inputKey: any) => {
                    try {
                        let event: any = {
                            target: {
                                name: inputKey.name,
                                required: inputKey.required,
                                value: input[inputKey.name].trim(),
                            }
                        }

                        onInputBlur(event)
                    } catch (error) {
                        // console.log(error);
                        // Override error
                    }
                })

                let { errors } = state

                errorKeys = [
                    'display_name', 'description'
                ]

                errorKeys.forEach((errorObject: any) => {
                    if (errors[errorObject].length > 0) {
                        valid = false
                    }
                })

                if (valid) {
                    setstate((prev: any) => ({
                        ...prev,
                        stage: 'configuration',
                    }))
                }
                return

            case 'configuration':
                if (!input.format) {
                    valid = false

                    setstate((prev: any) => ({
                        ...prev,
                        errors: {
                            ...prev.errors,
                            format: 'Kindly select a data format',
                            configuration: 'Kindly clear errors in section before proceeding',
                        },
                    }));
                }

                if (!valid) return

                const minLength = 3;
                const maxLength = 200;

                let updatedErrors = state.errors
                const minVal = Number(input.minimum);
                const maxVal = Number(input.maximum);

                const isMinValid = Number.isInteger(minVal);
                const isMaxValid = Number.isInteger(maxVal);

                if (input.minimum && !isMinValid) {
                    valid = false
                    updatedErrors.minimum = "Minimum length must be a whole number.";
                }

                if (input.maximum && !isMaxValid) {
                    valid = false
                    updatedErrors.maximum = "Maximum length must be a whole number.";
                }

                if (isMinValid && (minVal < minLength || minVal > maxLength)) {
                    valid = false
                    updatedErrors.minimum = `Minimum must be between ${minLength} and ${maxLength}.`;
                }

                if (isMaxValid && (maxVal < minLength || maxVal > maxLength)) {
                    valid = false
                    updatedErrors.maximum = `Maximum must be between ${minLength} and ${maxLength}.`;
                }

                if (isMinValid && isMaxValid && minVal > maxVal) {
                    valid = false
                    updatedErrors.maximum = "Maximum must be greater than or equal to minimum.";
                }

                if (!valid) {
                    updatedErrors.configuration = 'Kindly clear errors in section before proceeding'
                } else {
                    updatedErrors.configuration = ''
                }

                setstate((prev: any) => ({
                    ...prev,
                    stage: valid ? 'leadership' : 'configuration',
                    errors: updatedErrors
                }));
                break

            default:
                break;
        }
    }

    const onCreateAttributeFormHandler = (e: any) => {
        e.preventDefault()
        let { posting, stage, input } = state
        if (posting) return

        let valid = true
        let inputKeys = null
        let errorKeys = null

        switch (stage) {
            case 'details':
                if (!input.attr_group) {
                    setstate((prev: any) => ({
                        ...prev,
                        errors: {
                            ...prev.errors,
                            attr_group: 'Kindly select an attribute group',
                            details: 'Kindly clear errors in section before proceeding',
                        },
                    }));

                    return
                }

                inputKeys = [
                    'display_name',
                    'description',
                ]

                inputKeys.forEach((inputKey: any) => {
                    try {
                        let oTarget = e.target[inputKey]

                        let event: any = {
                            target: {
                                name: oTarget.name,
                                required: oTarget.required,
                                value: oTarget.value.trim(),
                            }
                        }

                        onInputBlur(event)
                    } catch (error) {
                        // console.log(error);
                        // Override error
                    }
                })

                let { errors } = state

                errorKeys = [
                    'display_name', 'description'
                ]

                errorKeys.forEach((errorObject: any) => {
                    if (errors[errorObject].length > 0) {
                        valid = false
                    }
                })

                if (valid) {
                    setstate((prev: any) => ({
                        ...prev,
                        stage: 'configuration',
                    }))
                }
                return

            default:
                break;
        }
    }

    return (
        <React.Fragment>
            <Helmet>
                <title>{pageTitle}</title>
            </Helmet>

            <div className="flex flex-col gap-x-3 w-full mx-auto h-full">
                {
                    state.status === 'rejected' ? (
                        <div className="py-2 px-6 w-full mx-auto flex-1 flex flex-col justify-center">
                            <ErrorState
                                code={state.httpStatus}
                            />
                        </div>
                    ) : state.status === 'fulfilled' ? (
                        <form className="rounded-md shadow-none space-y-px flex flex-col w-full h-full" onSubmit={onCreateAttributeFormHandler}>
                            <div className="w-full py-2 border-b border-slate-200 dark:border-slate-600 lg:px-6 px-5 flex flex-col">
                                <div className="flex-none">
                                    <ModuleBreadCrumb breadCrumb={breadCrumb} />
                                </div>
                            </div>

                            <div className="w-full md:max-w-[768px] lg:px-6 px-4 pt-3">
                                <header className="w-full mb-2 header-title">
                                    <p className="text-sm pb-4 text-slate-500 dark:text-slate-400">
                                        Create and manage user-based attributes to capture the data that matters.
                                    </p>
                                </header>
                            </div>

                            <div className="w-full md:max-w-[640px] flex min-h-full flex-col pb-20 gap-2 px-5 md:px-0">
                                <div className={
                                    clsx(
                                        state.stage === 'details' ? "" : "hover:bg-slate-100 dark:hover:bg-slate-900/40 cursor-pointer",
                                        "w-full flex flex-row align-middle gap-x-4 py-1.5 md:pl-6"
                                    )
                                } aria-label="Attribute Details Title">
                                    <div className={clsx(
                                        state.errors.details.length > 0
                                            ? 'bg-red-500 dark:bg-red-800 text-white'
                                            : state.stage === 'details'
                                                ? 'bg-orange-400 dark:bg-orange-800 text-white'
                                                : 'bg-slate-500 dark:bg-slate-900 dark:text-slate-500',
                                        'w-7 h-7 text-white rounded-full flex flex-col justify-center items-center'
                                    )}
                                    >
                                        <span className="block">1</span>
                                    </div>

                                    <div className="flex-1">
                                        <span className={
                                            clsx(
                                                state.errors.details.length > 0 ? "text-red-700 dark:text-red-500" : "text-slate-700 dark:text-slate-200",
                                                "text-base"
                                            )
                                        }>
                                            Attribute Details
                                        </span>
                                    </div>
                                </div>

                                <div aria-label="Attribute Details Form" className="w-full md:pt-0 flex flex-row align-middle gap-x-4 md:pl-6">
                                    <div className="w-7 h-auto relative hidden md:flex flex-col justify-center items-center">
                                        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gray-300 dark:bg-slate-600 transform -translate-x-1/2"></div>
                                    </div>

                                    <div className={`flex-1 ${state.stage === 'details' ? "py-0" : "py-3"}`}>
                                        <div className={
                                            clsx(
                                                state.stage === 'details' ? "" : "hidden",
                                                "flex-1"
                                            )
                                        }>
                                            <div className="w-full mb-2 pb-3 md:max-w-md">
                                                <span className="block text-xs text-slate-600 dark:text-slate-300 pb-2">
                                                    Select the most appropriate group to place this attribute into.
                                                </span>

                                                <ListBox
                                                    value={state.input.attr_group ? state.input.attr_group.g : ''}
                                                    eventHandler={(e: any) => onListBoxHandler(e, 'attr_group')}
                                                    button={
                                                        <>
                                                            <ListboxButton className="relative border w-full rounded-md py-2 pr-2 text-left cursor-default focus:ring-primary focus:border-primary sm:text-sm ring-slate-300 dark:ring-slate-600 focus:ring-orange-600 focus:outline-none focus:border-0 focus:ring-primary focus:outline-orange-500 border-slate-300 dark:border-slate-600 dark:text-white">
                                                                <div className="flex w-full items-center align-middle">
                                                                    <div className="flex-grow flex items-center">
                                                                        <span className="truncate ml-3 font-normal group-data-[selected]:font-semibold">
                                                                            {
                                                                                state.input.attr_group ? (
                                                                                    <div className="flex items-center gap-x-4">
                                                                                        <span className="truncate group-data-[selected]:font-semibold">
                                                                                            {state.input.attr_group.e}
                                                                                        </span>
                                                                                    </div>
                                                                                ) : (
                                                                                    <>
                                                                                        Attribute Group
                                                                                    </>
                                                                                )
                                                                            }
                                                                        </span>
                                                                    </div>

                                                                    <span className="fa-light fa-chevron-down focus:text-primary float-end"></span>
                                                                </div>
                                                            </ListboxButton>
                                                        </>
                                                    }
                                                    options={
                                                        <>
                                                            {
                                                                state.data.groups.length > 0 ? (
                                                                    state.data.groups.map((group: any) => (
                                                                        group.n && (
                                                                            <ListboxOption key={`usr-attr-band-${group.g}`} value={group.g} className="group relative cursor-default select-none py-2 pl-3 pr-9 data-[selected]:text-orange-600 data-[focus]:bg-orange-100 dark:data-[focus]:bg-orange-900/70 data-[focus]:text-orange-700 dark:data-[focus]:text-orange-500 rounded-md w-full">
                                                                                <div className="flex items-center gap-x-4">
                                                                                    <span className="truncate group-data-[selected]:font-medium">
                                                                                        {group.e}

                                                                                        <span className="block text-xs text-slate-500 dark:text-slate-400">
                                                                                            {group.t}
                                                                                        </span>
                                                                                    </span>
                                                                                </div>

                                                                                <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-orange-600 group-[&:not([data-selected])]:hidden">
                                                                                    <span className="fa-light fa-check"></span>
                                                                                </span>
                                                                            </ListboxOption>
                                                                        )
                                                                    ))
                                                                ) : (
                                                                    <ListboxOption value={null} className="group relative cursor-default select-none py-2.5 pl-3 pr-9 text-slate-500 data-[focus]:bg-slate-100 dark:data-[focus]:bg-slate-900/70 data-[focus]:text-slate-500 dark:data-[focus]:text-slate-500 rounded-md w-full">
                                                                        <div className="flex items-center gap-x-4">
                                                                            <span className="truncate group-data-[selected]:font-medium">
                                                                                No Attribute Groups Found
                                                                            </span>
                                                                        </div>

                                                                        <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-600 group-[&:not([data-selected])]:hidden">
                                                                            <span className="fa-light fa-check"></span>
                                                                        </span>
                                                                    </ListboxOption>
                                                                )
                                                            }

                                                            <div className="my-1 w-[95%] mx-auto border-b border-slate-300 dark:border-slate-600"></div>

                                                            <ListboxOption value={'__create_new__'} className="group relative cursor-default select-none py-2.5 pl-3 pr-9 text-orange-600 data-[focus]:bg-orange-100 dark:data-[focus]:bg-orange-900/70 data-[focus]:text-orange-700 dark:data-[focus]:text-orange-500 rounded-md w-full">
                                                                <div className="flex items-center gap-x-4">
                                                                    <span className="truncate group-data-[selected]:font-medium">
                                                                        Create Attribute Group
                                                                    </span>
                                                                </div>

                                                                <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-orange-600 group-[&:not([data-selected])]:hidden">
                                                                    <span className="fa-light fa-check"></span>
                                                                </span>
                                                            </ListboxOption>
                                                        </>
                                                    }
                                                />

                                                {
                                                    state.errors.attr_group.length > 0 && (
                                                        <span className='invalid-feedback text-xs text-red-600 dark:text-red-400 pl-1 pt-1'>
                                                            {state.errors.attr_group}
                                                        </span>
                                                    )
                                                }
                                            </div>

                                            <div className="w-full pb-3">
                                                <span className="block text-xs text-slate-600 dark:text-slate-300 pb-3">
                                                    Name the attribute to match your organization's language or use case.
                                                </span>

                                                <FloatingInput
                                                    required={true}
                                                    name={"display_name"}
                                                    posting={state.posting}
                                                    label={"Display Name *"}
                                                    blurHandler={onInputBlur}
                                                    changeHandler={onChangeHandler}
                                                    value={state.input.display_name}
                                                    error={state.errors.display_name}
                                                />
                                            </div>

                                            <div className="w-full pb-3">
                                                <p className="text-xs pb-3 text-slate-500 dark:text-slate-400">
                                                    A brief description for others to understand the purpose of this attribute
                                                </p>

                                                <FloatingTextArea
                                                    required={false}
                                                    name={"description"}
                                                    posting={state.posting}
                                                    label={
                                                        <span className="flex items-center gap-1">
                                                            Description <span className="text-xs text-gray-500 dark:text-gray-400">(Optional)</span>
                                                        </span>
                                                    }
                                                    blurHandler={onInputBlur}
                                                    changeHandler={onChangeHandler}
                                                    value={state.input.description}
                                                    error={state.errors.description}
                                                />
                                            </div>

                                            {
                                                state.errors.details.length > 0 && (
                                                    <span className='text-xs py-2 px-2 rounded-md my-3 flex align-middle items-center bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 gap-x-2'>
                                                        <span className="fa-regular fa-exclamation-circle fa-lg"></span>

                                                        {state.errors.details}
                                                    </span>
                                                )
                                            }

                                            <div className="py-3 flex md:flex-row flex-row-reverse">
                                                <div className="flex md:flex-row flex-row-reverse items-center align-middle gap-x-2">
                                                    <button type="button" onClick={sectionValidationHandler} className="w-auto min-w-20 justify-center disabled:cursor-not-allowed text-sm rounded-md border border-orange-500 hover:border-orange-600 dark:border-transparent shadow-sm px-2 py-1 bg-inherit dark:bg-orange-600 text-orange-600 dark:text-white hover:text-orange-700 dark:hover:text-white dark:hover:bg-orange-700 focus:outline-none">
                                                        Next
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className={
                                    clsx(
                                        state.stage === 'configuration' ? "" : "hover:bg-slate-100 dark:hover:bg-slate-900/40 cursor-pointer",
                                        "w-full flex flex-row align-middle gap-x-4 py-1.5 md:pl-6"
                                    )
                                } aria-label="Configurations Title">
                                    <div className={clsx(
                                        state.errors.configuration.length > 0
                                            ? 'bg-red-500 dark:bg-red-800 text-white'
                                            : state.stage === 'configuration'
                                                ? 'bg-orange-400 dark:bg-orange-800 text-white'
                                                : 'bg-slate-500 dark:bg-slate-900 dark:text-slate-500',
                                        'w-7 h-7 text-white rounded-full flex flex-col justify-center items-center'
                                    )}
                                    >
                                        <span className="block">2</span>
                                    </div>

                                    <div className="flex-1">
                                        <span className={
                                            clsx(
                                                state.errors.configuration.length > 0 ? "text-red-700 dark:text-red-500" : "text-slate-700 dark:text-slate-200",
                                                "text-base"
                                            )
                                        }>
                                            Configurations

                                            {
                                                state.stage === 'configuration' && (
                                                    <span className="block text-xs pt-1 text-slate-500 dark:text-slate-400">
                                                        Customize how this attribute behaves
                                                    </span>
                                                )
                                            }
                                        </span>
                                    </div>
                                </div>

                                <div aria-label="Configurations Form" className="w-full md:pt-0 flex flex-row align-middle gap-x-4 md:pl-6">
                                    <div className="w-7 h-auto relative hidden md:flex flex-col justify-center items-center">
                                        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gray-300 dark:bg-slate-600 transform -translate-x-1/2"></div>
                                    </div>

                                    <div className={`flex-1 ${state.stage === 'configuration' ? "py-0" : "py-3"}`}>
                                        <div className={
                                            clsx(
                                                state.stage === 'configuration' ? "" : "hidden",
                                                "flex-1 text-sm text-slate-500 dark:text-slate-400"
                                            )
                                        }>
                                            <div className="w-full pb-3">
                                                <p className='label flex items-center text-slate-700 dark:text-slate-300 text-sm pb-1 font-medium'>
                                                    Data Format
                                                </p>

                                                <p className='label items-center text-slate-500 dark:text-slate-400 text-sm pb-4'>
                                                    Specify the data format this attribute should accept
                                                </p>

                                                <ListBox
                                                    value={state.input.format ? state.input.format.key : ''}
                                                    eventHandler={(e: any) => onListBoxHandler(e, 'format')}
                                                    button={
                                                        <>
                                                            <ListboxButton className="relative border w-full rounded-md py-2 pr-2 text-left cursor-default focus:ring-primary focus:border-primary sm:text-sm ring-slate-300 dark:ring-slate-600 focus:ring-orange-600 focus:outline-none focus:border-0 focus:ring-primary focus:outline-orange-500 border-slate-300 dark:border-slate-600 dark:text-white">
                                                                <div className="flex w-full items-center align-middle">
                                                                    <div className="flex-grow flex items-center">
                                                                        <span className="truncate ml-3 font-normal group-data-[selected]:font-semibold">
                                                                            {
                                                                                state.input.format ? (
                                                                                    <div className="flex items-center gap-x-4">
                                                                                        <span className="truncate group-data-[selected]:font-semibold">
                                                                                            {state.input.format.label}
                                                                                        </span>
                                                                                    </div>
                                                                                ) : (
                                                                                    <>
                                                                                        Data Format
                                                                                    </>
                                                                                )
                                                                            }
                                                                        </span>
                                                                    </div>

                                                                    <span className="fa-light fa-chevron-down focus:text-primary float-end"></span>
                                                                </div>
                                                            </ListboxButton>
                                                        </>
                                                    }
                                                    options={
                                                        <>
                                                            {
                                                                dataFormatsArray.map((dataType: any) => (
                                                                    <ListboxOption key={`attr-data-type-${dataType.key}`} value={dataType} className="group relative cursor-default select-none py-2 pl-3 pr-9 data-[selected]:text-orange-600 data-[focus]:bg-orange-100 dark:data-[focus]:bg-orange-900/70 data-[focus]:text-orange-700 dark:data-[focus]:text-orange-500 rounded-md w-full">
                                                                        <div className="flex items-center gap-x-4">
                                                                            <span className="truncate group-data-[selected]:font-medium">
                                                                                {dataType.label}

                                                                                <span className="block text-xs text-slate-500 dark:text-slate-400">
                                                                                    {dataType.desc}
                                                                                </span>
                                                                            </span>
                                                                        </div>

                                                                        <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-orange-600 group-[&:not([data-selected])]:hidden">
                                                                            <span className="fa-light fa-check"></span>
                                                                        </span>
                                                                    </ListboxOption>
                                                                ))
                                                            }
                                                        </>
                                                    }
                                                />

                                                {
                                                    state.errors.format.length > 0 && (
                                                        <span className='invalid-feedback text-xs text-red-600 dark:text-red-400 pl-1 pt-1'>
                                                            {state.errors.format}
                                                        </span>
                                                    )
                                                }
                                            </div>

                                            <AttributeRestrictions
                                                state={state}
                                                setstate={setstate}
                                                onInputBlur={onInputBlur}
                                                onChangeHandler={onChangeHandler}
                                                onListBoxHandler={onListBoxHandler}
                                            />

                                            {
                                                state.errors.configuration.length > 0 && (
                                                    <span className='text-xs py-2 px-2 rounded-md my-3 flex align-middle items-center bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 gap-x-2'>
                                                        <span className="fa-regular fa-exclamation-circle fa-lg"></span>

                                                        {state.errors.configuration}
                                                    </span>
                                                )
                                            }

                                            <div className="py-3 flex md:flex-row flex-row-reverse">
                                                <div className="flex md:flex-row flex-row-reverse items-center align-middle gap-x-2">
                                                    <button type="button" onClick={sectionValidationHandler} className="w-auto min-w-20 justify-center disabled:cursor-not-allowed text-sm rounded-md border border-orange-500 hover:border-orange-600 dark:border-transparent shadow-sm px-2 py-1 bg-inherit dark:bg-orange-600 text-orange-600 dark:text-white hover:text-orange-700 dark:hover:text-white dark:hover:bg-orange-700 focus:outline-none">
                                                        Next
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className={
                                    clsx(
                                        state.stage === 'leadership' ? "" : "hover:bg-slate-100 dark:hover:bg-slate-900/40 cursor-pointer",
                                        "w-full flex flex-row align-middle gap-x-4 py-1.5 md:pl-6"
                                    )
                                } aria-label="Configurations Title">
                                    <div className={clsx(
                                        state.errors.leadership.length > 0
                                            ? 'bg-red-500 dark:bg-red-800 text-white'
                                            : state.stage === 'leadership'
                                                ? 'bg-orange-400 dark:bg-orange-800 text-white'
                                                : 'bg-slate-500 dark:bg-slate-900 dark:text-slate-500',
                                        'w-7 h-7 text-white rounded-full flex flex-col justify-center items-center'
                                    )}
                                    >
                                        <span className="block">3</span>
                                    </div>

                                    <div className="flex-1">
                                        <span className={
                                            clsx(
                                                state.errors.leadership.length > 0 ? "text-red-700 dark:text-red-500" : "text-slate-700 dark:text-slate-200",
                                                "text-lg"
                                            )
                                        }>
                                            Leadership Assignment

                                            {
                                                state.stage === 'configuration' && (
                                                    <span className="block text-xs pt-1 text-slate-500 dark:text-slate-400">
                                                        Specify if leadership oversight is required for this attribute
                                                    </span>
                                                )
                                            }
                                        </span>
                                    </div>
                                </div>

                                <div aria-label="Leadership Assignment Form" className="w-full md:pt-0 flex flex-row align-middle gap-x-4 md:pl-6">
                                    <div className="w-7 h-auto relative hidden md:flex flex-col justify-center items-center">
                                        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gray-300 dark:bg-slate-600 transform -translate-x-1/2"></div>
                                    </div>

                                    <div className={`flex-1 ${state.stage === 'leadership' ? "py-0" : "py-3"}`}>
                                        <div className={
                                            clsx(
                                                state.stage === 'leadership' ? "" : "hidden",
                                                "flex-1 text-sm text-slate-500 dark:text-slate-400"
                                            )
                                        }>
                                            <div className="w-full pb-3">
                                                <p className='label flex items-center text-slate-700 dark:text-slate-300 text-sm pb-1'>
                                                    Specify if leadership oversight is required for this attribute
                                                </p>



                                            </div>
                                        </div>
                                    </div>
                                </div>




                            </div>


                        </form>
                    ) : (
                        <div className="flex-1 flex flex-col justify-center w-full overflow-auto py-4">
                            <LoadingXL />
                        </div>
                    )
                }
            </div>

            <CreateOrEditAttributeGroup
                cargo={state.cargo}
                onComplete={onCompleteActionHandler}
                show={state.show.CreateAttributeGroup}
                showOrHide={() => toggleModal('CreateAttributeGroup', false)}
            />
        </React.Fragment>
    )
}
